import React, { useState } from 'react';
import './Docs.css';

type Language = 'curl' | 'fetch' | 'axios';

function Docs() {
  const [activeSection, setActiveSection] = useState('intro');
  const [language, setLanguage] = useState<Language>('curl');

  // Get API URL using same pattern as other pages
  const API_URL = (window as any).ENV?.REACT_APP_API_URL || process.env.REACT_APP_API_URL || '/api';
  const baseURL = API_URL.endsWith('/api') ? API_URL : API_URL + '/api';

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const getCode = (endpoint: string, method: string, body?: any, isMultipart: boolean = false, multipartFields?: string[]): string => {
    const url = `${baseURL}${endpoint}`;
    const token = '{your_access_token}';

    if (language === 'curl') {
      let cmd = `curl -X ${method} "${url}"`;
      if (method !== 'GET' && method !== 'DELETE') {
        if (isMultipart) {
          cmd += ` \\\n  -H "Authorization: Bearer ${token}"`;
          if (multipartFields) {
            multipartFields.forEach(f => { cmd += ` \\\n  -F "${f}"`; });
          } else {
            cmd += ` \\\n  -F "file=@image.jpg"`;
          }
        } else {
          cmd += ` \\\n  -H "Content-Type: application/json"`;
          cmd += ` \\\n  -H "Authorization: Bearer ${token}"`;
          if (body) {
            cmd += ` \\\n  -d '${JSON.stringify(body, null, 2)}'`;
          }
        }
      } else {
        cmd += ` \\\n  -H "Authorization: Bearer ${token}"`;
      }
      return cmd;
    } else if (language === 'fetch') {
      if (isMultipart) {
        return `const formData = new FormData();
${multipartFields ? multipartFields.map(f => {
  const parts = f.split('=');
  return `formData.append('${parts[0]}', ${parts[1] || 'imageFile'});`;
}).join('\n') : 'formData.append(\'file\', imageFile);'}

fetch('${url}', {
  method: '${method}',
  headers: { 'Authorization': 'Bearer ${token}' },
  body: formData
})
  .then(res => res.json())
  .then(data => console.log(data))
  .catch(err => console.error(err));`;
      }
      const options: any = {
        method,
        headers: { 'Authorization': `Bearer ${token}` } as any
      };
      if (method !== 'GET' && method !== 'DELETE') {
        options.headers['Content-Type'] = 'application/json';
        if (body) options.body = JSON.stringify(body, null, 2);
      }
      return `fetch('${url}', ${JSON.stringify(options, null, 2)})
  .then(res => res.json())
  .then(data => console.log(data))
  .catch(err => console.error(err));`;
    } else {
      // axios
      if (method === 'GET' || method === 'DELETE') {
        return `axios.${method.toLowerCase()}('${url}', {
  headers: { 'Authorization': 'Bearer ${token}' }
})
  .then(res => console.log(res.data))
  .catch(err => console.error(err));`;
      }
      if (isMultipart) {
        return `const formData = new FormData();
${multipartFields ? multipartFields.map(f => {
  const parts = f.split('=');
  return `formData.append('${parts[0]}', ${parts[1] || 'imageFile'});`;
}).join('\n') : 'formData.append(\'file\', imageFile);'}

axios.${method.toLowerCase()}('${url}', formData, {
  headers: {
    'Authorization': 'Bearer ${token}',
    'Content-Type': 'multipart/form-data'
  }
})
  .then(res => console.log(res.data))
  .catch(err => console.error(err));`;
      }
      return `axios.${method.toLowerCase()}('${url}', ${body ? JSON.stringify(body, null, 2) : '{}'}, {
  headers: {
    'Content-Type': 'application/json',
    'Authorization': 'Bearer ${token}'
  }
})
  .then(res => console.log(res.data))
  .catch(err => console.error(err));`;
    }
  };

  const sections = [
    { id: 'intro', title: 'Introdução' },
    { id: 'tokens', title: 'Tokens' },
    { id: 'instances', title: 'Instâncias' },
    { id: 'organizations', title: 'Organizações' },
    { id: 'people', title: 'Pessoas' },
    { id: 'search', title: 'Busca Facial' },
    { id: 'errors', title: 'Erros' },
    { id: 'config', title: 'Configuração' },
  ];

  return (
    <div className="docs-page">
      <aside className="docs-sidebar">
        <div className="sidebar-content">
          <h2>Documentação</h2>
          <nav className="docs-nav">
            {sections.map(section => (
              <button
                key={section.id}
                className={activeSection === section.id ? 'active' : ''}
                onClick={() => setActiveSection(section.id)}
              >
                {section.title}
              </button>
            ))}
          </nav>
          
          <div className="language-selector">
            <label>Linguagem:</label>
            <select value={language} onChange={(e) => setLanguage(e.target.value as Language)}>
              <option value="curl">cURL</option>
              <option value="fetch">Fetch API</option>
              <option value="axios">Axios</option>
            </select>
          </div>
        </div>
      </aside>

      <main className="docs-main">
        {/* ==================== INTRODUÇÃO ==================== */}
        {activeSection === 'intro' && (
          <section>
            <h1>Documentação da API FaceAPI</h1>
            <p className="intro-text">
              Bem-vindo à documentação da API FaceAPI. Esta API permite gerenciar organizações,
              pessoas e realizar buscas faciais usando tecnologia de reconhecimento facial avançada.
            </p>
            
            <div className="info-box">
              <h3>🔗 URL Base</h3>
              <code>{baseURL}</code>
            </div>

            <div className="info-box">
              <h3>📋 Formato</h3>
              <p>Requisições e respostas utilizam JSON, exceto upload de imagens que usa multipart/form-data.</p>
            </div>

            <div className="warning-box">
              <h3>🔐 Autenticação</h3>
              <p>Todos os endpoints (exceto login e token inicial) requerem um token no header:</p>
              <code>Authorization: Bearer {'{your_access_token}'}</code>
            </div>

            <div className="info-box">
              <h3>📌 Recursos da API</h3>
              <ul>
                <li><strong>Tokens</strong> — Gerenciamento de tokens de integração</li>
                <li><strong>Instâncias</strong> — Configuração de servidores (MongoDB + Milvus)</li>
                <li><strong>Organizações</strong> — Agrupamento de pessoas com storage isolado</li>
                <li><strong>Pessoas</strong> — Cadastro, imagens e gerenciamento de pessoas</li>
                <li><strong>Busca Facial</strong> — Pesquisa por similaridade e comparação de imagens</li>
              </ul>
            </div>
          </section>
        )}

        {/* ==================== TOKENS ==================== */}
        {activeSection === 'tokens' && (
          <section>
            <h1>Tokens</h1>
            <p>Gerenciamento de tokens de integração para autenticação via API.</p>

            <div className="endpoint-card">
              <div className="endpoint-header">
                <span className="method method-get">GET</span>
                <code className="endpoint-path">/api/tokens</code>
              </div>
              <p className="endpoint-description">Lista todos os tokens.</p>

              <h4>Query Parameters</h4>
              <table className="params-table">
                <thead><tr><th>Parâmetro</th><th>Tipo</th><th>Padrão</th><th>Descrição</th></tr></thead>
                <tbody>
                  <tr><td><code>skip</code></td><td>integer</td><td>0</td><td>Registros para pular</td></tr>
                  <tr><td><code>limit</code></td><td>integer</td><td>100</td><td>Máximo de registros</td></tr>
                  <tr><td><code>sort_by</code></td><td>string</td><td>created_at</td><td>Campo: name | created_at</td></tr>
                  <tr><td><code>sort_order</code></td><td>string</td><td>desc</td><td>Ordem: asc | desc</td></tr>
                </tbody>
              </table>

              <h4>Exemplo</h4>
              <div className="code-container">
                <button className="copy-btn" onClick={() => copyToClipboard(getCode('/tokens?skip=0&limit=100', 'GET'))}>📋 Copiar</button>
                <pre className="code-block"><code>{getCode('/tokens?skip=0&limit=100', 'GET')}</code></pre>
              </div>
            </div>

            <div className="endpoint-card">
              <div className="endpoint-header">
                <span className="method method-post">POST</span>
                <code className="endpoint-path">/api/tokens</code>
              </div>
              <p className="endpoint-description">Cria um novo token. O valor do token é gerado automaticamente. Nomes duplicados retornam 409.</p>

              <h4>Request Body</h4>
              <pre className="code-block">
                <code>{JSON.stringify({ name: "Meu Token", active: true }, null, 2)}</code>
              </pre>

              <h4>Response (201 Created)</h4>
              <pre className="code-block">
                <code>{JSON.stringify({
                  _id: "507f1f77bcf86cd799439011",
                  name: "Meu Token",
                  token: "gerado_automaticamente_abc123",
                  active: true,
                  created_at: "2025-01-01T00:00:00Z"
                }, null, 2)}</code>
              </pre>

              <h4>Exemplo</h4>
              <div className="code-container">
                <button className="copy-btn" onClick={() => copyToClipboard(getCode('/tokens', 'POST', { name: "Meu Token", active: true }))}>📋 Copiar</button>
                <pre className="code-block"><code>{getCode('/tokens', 'POST', { name: "Meu Token", active: true })}</code></pre>
              </div>
            </div>

            <div className="endpoint-card">
              <div className="endpoint-header">
                <span className="method method-put">PUT</span>
                <code className="endpoint-path">/api/tokens/{'{token_id}'}</code>
              </div>
              <p className="endpoint-description">Atualiza nome ou status de um token. Nomes duplicados retornam 409.</p>

              <h4>Request Body</h4>
              <pre className="code-block">
                <code>{JSON.stringify({ name: "Novo Nome", active: false }, null, 2)}</code>
              </pre>

              <h4>Exemplo</h4>
              <div className="code-container">
                <button className="copy-btn" onClick={() => copyToClipboard(getCode('/tokens/507f1f77bcf86cd799439011', 'PUT', { name: "Novo Nome", active: false }))}>📋 Copiar</button>
                <pre className="code-block"><code>{getCode('/tokens/507f1f77bcf86cd799439011', 'PUT', { name: "Novo Nome", active: false })}</code></pre>
              </div>
            </div>

            <div className="endpoint-card">
              <div className="endpoint-header">
                <span className="method method-delete">DELETE</span>
                <code className="endpoint-path">/api/tokens/{'{token_id}'}</code>
              </div>
              <p className="endpoint-description">Remove um token.</p>
              <h4>Response (204 No Content)</h4>
              <h4>Exemplo</h4>
              <div className="code-container">
                <button className="copy-btn" onClick={() => copyToClipboard(getCode('/tokens/507f1f77bcf86cd799439011', 'DELETE'))}>📋 Copiar</button>
                <pre className="code-block"><code>{getCode('/tokens/507f1f77bcf86cd799439011', 'DELETE')}</code></pre>
              </div>
            </div>
          </section>
        )}

        {/* ==================== INSTÂNCIAS ==================== */}
        {activeSection === 'instances' && (
          <section>
            <h1>Instâncias</h1>
            <p>Configuração de servidores de banco de dados (MongoDB + Milvus). Cada organização é associada a uma instância.</p>

            <div className="endpoint-card">
              <div className="endpoint-header">
                <span className="method method-get">GET</span>
                <code className="endpoint-path">/api/instances</code>
              </div>
              <p className="endpoint-description">Lista todas as instâncias. Ordenadas por padrão primeiro, depois nome.</p>

              <h4>Query Parameters</h4>
              <table className="params-table">
                <thead><tr><th>Parâmetro</th><th>Tipo</th><th>Padrão</th><th>Descrição</th></tr></thead>
                <tbody>
                  <tr><td><code>skip</code></td><td>integer</td><td>0</td><td>Registros para pular</td></tr>
                  <tr><td><code>limit</code></td><td>integer</td><td>100</td><td>Máximo de registros</td></tr>
                </tbody>
              </table>

              <h4>Response (200 OK)</h4>
              <pre className="code-block">
                <code>{JSON.stringify([{
                  _id: "507f1f77bcf86cd799439011",
                  name: "Servidor Principal",
                  mongo_url: "mongodb://admin:password@mongodb:27017",
                  milvus_url: "milvus:19530",
                  active: true,
                  is_default: true,
                  created_at: "2025-01-01T00:00:00Z"
                }], null, 2)}</code>
              </pre>

              <h4>Exemplo</h4>
              <div className="code-container">
                <button className="copy-btn" onClick={() => copyToClipboard(getCode('/instances', 'GET'))}>📋 Copiar</button>
                <pre className="code-block"><code>{getCode('/instances', 'GET')}</code></pre>
              </div>
            </div>

            <div className="endpoint-card">
              <div className="endpoint-header">
                <span className="method method-post">POST</span>
                <code className="endpoint-path">/api/instances</code>
              </div>
              <p className="endpoint-description">Cria uma nova instância. Nomes duplicados retornam 409. Se não existir nenhuma padrão, será marcada automaticamente.</p>

              <h4>Request Body</h4>
              <pre className="code-block">
                <code>{JSON.stringify({
                  name: "Servidor Principal",
                  mongo_url: "mongodb://admin:password@mongodb:27017",
                  milvus_url: "milvus:19530",
                  active: true,
                  is_default: true
                }, null, 2)}</code>
              </pre>

              <h4>Exemplo</h4>
              <div className="code-container">
                <button className="copy-btn" onClick={() => copyToClipboard(getCode('/instances', 'POST', {
                  name: "Servidor Principal",
                  mongo_url: "mongodb://admin:password@mongodb:27017",
                  milvus_url: "milvus:19530",
                  active: true,
                  is_default: true
                }))}>📋 Copiar</button>
                <pre className="code-block"><code>{getCode('/instances', 'POST', {
                  name: "Servidor Principal",
                  mongo_url: "mongodb://admin:password@mongodb:27017",
                  milvus_url: "milvus:19530",
                  active: true,
                  is_default: true
                })}</code></pre>
              </div>
            </div>

            <div className="endpoint-card">
              <div className="endpoint-header">
                <span className="method method-put">PUT</span>
                <code className="endpoint-path">/api/instances/{'{instance_id}'}</code>
              </div>
              <p className="endpoint-description">Atualiza uma instância. Todos os campos são opcionais. Nomes duplicados retornam 409.</p>

              <h4>Exemplo</h4>
              <div className="code-container">
                <button className="copy-btn" onClick={() => copyToClipboard(getCode('/instances/507f1f77bcf86cd799439011', 'PUT', { name: "Novo Nome", is_default: true }))}>📋 Copiar</button>
                <pre className="code-block"><code>{getCode('/instances/507f1f77bcf86cd799439011', 'PUT', { name: "Novo Nome", is_default: true })}</code></pre>
              </div>
            </div>

            <div className="endpoint-card">
              <div className="endpoint-header">
                <span className="method method-delete">DELETE</span>
                <code className="endpoint-path">/api/instances/{'{instance_id}'}</code>
              </div>
              <p className="endpoint-description">Remove uma instância.</p>
              <h4>Response (204 No Content)</h4>
              <h4>Exemplo</h4>
              <div className="code-container">
                <button className="copy-btn" onClick={() => copyToClipboard(getCode('/instances/507f1f77bcf86cd799439011', 'DELETE'))}>📋 Copiar</button>
                <pre className="code-block"><code>{getCode('/instances/507f1f77bcf86cd799439011', 'DELETE')}</code></pre>
              </div>
            </div>
          </section>
        )}

        {/* ==================== ORGANIZAÇÕES ==================== */}
        {activeSection === 'organizations' && (
          <section>
            <h1>Organizações</h1>
            <p>Organizações agrupam pessoas e possuem storage isolado (MongoDB database + Milvus collection). Cada organização possui um campo <code>key</code> obrigatório e único.</p>

            <div className="endpoint-card">
              <div className="endpoint-header">
                <span className="method method-get">GET</span>
                <code className="endpoint-path">/api/organizations</code>
              </div>
              <p className="endpoint-description">Lista todas as organizações.</p>

              <h4>Query Parameters</h4>
              <table className="params-table">
                <thead><tr><th>Parâmetro</th><th>Tipo</th><th>Padrão</th><th>Descrição</th></tr></thead>
                <tbody>
                  <tr><td><code>skip</code></td><td>integer</td><td>0</td><td>Registros para pular</td></tr>
                  <tr><td><code>limit</code></td><td>integer</td><td>100</td><td>Máximo de registros</td></tr>
                  <tr><td><code>sort_by</code></td><td>string</td><td>name</td><td>Campo: key | name | created_at</td></tr>
                  <tr><td><code>sort_order</code></td><td>string</td><td>asc</td><td>Ordem: asc | desc</td></tr>
                </tbody>
              </table>

              <h4>Response (200 OK)</h4>
              <pre className="code-block">
                <code>{JSON.stringify([{
                  _id: "507f1f77bcf86cd799439011",
                  key: "acme_corp",
                  name: "Acme Corp",
                  instance_id: "507f1f77bcf86cd799439012",
                  active: true,
                  created_at: "2025-01-01T00:00:00Z",
                  updated_at: "2025-01-01T00:00:00Z"
                }], null, 2)}</code>
              </pre>

              <h4>Exemplo</h4>
              <div className="code-container">
                <button className="copy-btn" onClick={() => copyToClipboard(getCode('/organizations?sort_by=key&sort_order=asc', 'GET'))}>📋 Copiar</button>
                <pre className="code-block"><code>{getCode('/organizations?sort_by=key&sort_order=asc', 'GET')}</code></pre>
              </div>
            </div>

            <div className="endpoint-card">
              <div className="endpoint-header">
                <span className="method method-get">GET</span>
                <code className="endpoint-path">/api/organizations/by-name?name={'{name}'}</code>
              </div>
              <p className="endpoint-description">Busca organização por nome exato (case-insensitive).</p>

              <h4>Query Parameters</h4>
              <table className="params-table">
                <thead><tr><th>Parâmetro</th><th>Tipo</th><th>Obrigatório</th><th>Descrição</th></tr></thead>
                <tbody>
                  <tr><td><code>name</code></td><td>string</td><td>Sim</td><td>Nome exato da organização</td></tr>
                </tbody>
              </table>

              <h4>Exemplo</h4>
              <div className="code-container">
                <button className="copy-btn" onClick={() => copyToClipboard(getCode('/organizations/by-name?name=Acme Corp', 'GET'))}>📋 Copiar</button>
                <pre className="code-block"><code>{getCode('/organizations/by-name?name=Acme Corp', 'GET')}</code></pre>
              </div>
            </div>

            <div className="endpoint-card">
              <div className="endpoint-header">
                <span className="method method-get">GET</span>
                <code className="endpoint-path">/api/organizations/{'{organization_id}'}</code>
              </div>
              <div className="endpoint-header">
                <span className="method method-get">GET</span>
                <code className="endpoint-path">/api/organizations/key/{'{organization_key}'}</code>
              </div>
              <p className="endpoint-description">Obtém detalhes de uma organização específica.</p>

              <h4>Exemplo</h4>
              <div className="code-container">
                <button className="copy-btn" onClick={() => copyToClipboard(getCode('/organizations/507f1f77bcf86cd799439011', 'GET'))}>📋 Copiar</button>
                <pre className="code-block"><code>{getCode('/organizations/507f1f77bcf86cd799439011', 'GET')}</code></pre>
              </div>

              <h4>Exemplo por key</h4>
              <div className="code-container">
                <button className="copy-btn" onClick={() => copyToClipboard(getCode('/organizations/key/acme_corp', 'GET'))}>📋 Copiar</button>
                <pre className="code-block"><code>{getCode('/organizations/key/acme_corp', 'GET')}</code></pre>
              </div>
            </div>

            <div className="endpoint-card">
              <div className="endpoint-header">
                <span className="method method-post">POST</span>
                <code className="endpoint-path">/api/organizations</code>
              </div>
              <p className="endpoint-description">Cria uma nova organização. O campo <code>key</code> é obrigatório e único (duplicatas retornam 409). Se instance_id não for fornecido, usa a instância padrão. O storage (MongoDB + Milvus) é criado automaticamente. Se a criação do storage falhar, a organização não é salva.</p>

              <h4>Request Body</h4>
              <pre className="code-block">
                <code>{JSON.stringify({
                  key: "acme_corp",
                  name: "Acme Corp",
                  instance_id: "507f1f77bcf86cd799439012",
                  active: true
                }, null, 2)}</code>
              </pre>

              <h4>Exemplo</h4>
              <div className="code-container">
                <button className="copy-btn" onClick={() => copyToClipboard(getCode('/organizations', 'POST', { key: "acme_corp", name: "Acme Corp", active: true }))}>📋 Copiar</button>
                <pre className="code-block"><code>{getCode('/organizations', 'POST', { key: "acme_corp", name: "Acme Corp", active: true })}</code></pre>
              </div>
            </div>

            <div className="endpoint-card">
              <div className="endpoint-header">
                <span className="method method-put">PUT</span>
                <code className="endpoint-path">/api/organizations/{'{organization_id}'}</code>
              </div>
              <div className="endpoint-header">
                <span className="method method-put">PUT</span>
                <code className="endpoint-path">/api/organizations/key/{'{organization_key}'}</code>
              </div>
              <p className="endpoint-description">Atualiza uma organização. O campo <code>key</code> é obrigatório. Campos opcionais: name, instance_id, active. Se key duplicada for informada, retorna 409.</p>

              <h4>Exemplo</h4>
              <div className="code-container">
                <button className="copy-btn" onClick={() => copyToClipboard(getCode('/organizations/507f1f77bcf86cd799439011', 'PUT', { key: "acme_corp_new", name: "Acme Corporation", active: false }))}>📋 Copiar</button>
                <pre className="code-block"><code>{getCode('/organizations/507f1f77bcf86cd799439011', 'PUT', { key: "acme_corp_new", name: "Acme Corporation", active: false })}</code></pre>
              </div>

              <h4>Exemplo por key</h4>
              <div className="code-container">
                <button className="copy-btn" onClick={() => copyToClipboard(getCode('/organizations/key/acme_corp', 'PUT', { key: "acme_corp_new", name: "Acme Corporation", active: false }))}>📋 Copiar</button>
                <pre className="code-block"><code>{getCode('/organizations/key/acme_corp', 'PUT', { key: "acme_corp_new", name: "Acme Corporation", active: false })}</code></pre>
              </div>
            </div>

            <div className="endpoint-card">
              <div className="endpoint-header">
                <span className="method method-delete">DELETE</span>
                <code className="endpoint-path">/api/organizations/{'{organization_id}'}</code>
              </div>
              <div className="endpoint-header">
                <span className="method method-delete">DELETE</span>
                <code className="endpoint-path">/api/organizations/key/{'{organization_key}'}</code>
              </div>
              <p className="endpoint-description">Remove uma organização.</p>
              <h4>Response (204 No Content)</h4>
              <h4>Exemplo</h4>
              <div className="code-container">
                <button className="copy-btn" onClick={() => copyToClipboard(getCode('/organizations/507f1f77bcf86cd799439011', 'DELETE'))}>📋 Copiar</button>
                <pre className="code-block"><code>{getCode('/organizations/507f1f77bcf86cd799439011', 'DELETE')}</code></pre>
              </div>

              <h4>Exemplo por key</h4>
              <div className="code-container">
                <button className="copy-btn" onClick={() => copyToClipboard(getCode('/organizations/key/acme_corp', 'DELETE'))}>📋 Copiar</button>
                <pre className="code-block"><code>{getCode('/organizations/key/acme_corp', 'DELETE')}</code></pre>
              </div>
            </div>
          </section>
        )}

        {/* ==================== PESSOAS ==================== */}
        {activeSection === 'people' && (
          <section>
            <h1>Pessoas</h1>
            <p>Gerenciamento de pessoas dentro de organizações. Cada pessoa tem uma chave (key) única na organização.</p>

            <div className="info-box">
              <h3>🔀 Identificador da organização</h3>
              <p>As rotas de pessoas aceitam os dois formatos:</p>
              <ul>
                <li><code>/api/organizations/{'{organization_id}'}/people</code> (compatibilidade)</li>
                <li><code>/api/organizations/key/{'{organization_key}'}/people</code> (integração por chave de negócio)</li>
              </ul>
              <p style={{marginTop: '10px'}}>Quando usar <code>organization_key</code>, as rotas com pessoa também usam <code>person_key</code> no path.</p>
            </div>

            <div className="endpoint-card">
              <div className="endpoint-header">
                <span className="method method-get">GET</span>
                <code className="endpoint-path">/api/organizations/{'{organization_id}'}/people</code>
              </div>
              <div className="endpoint-header">
                <span className="method method-get">GET</span>
                <code className="endpoint-path">/api/organizations/key/{'{organization_key}'}/people</code>
              </div>
              <p className="endpoint-description">Lista pessoas de uma organização.</p>

              <h4>Query Parameters</h4>
              <table className="params-table">
                <thead><tr><th>Parâmetro</th><th>Tipo</th><th>Padrão</th><th>Descrição</th></tr></thead>
                <tbody>
                  <tr><td><code>skip</code></td><td>integer</td><td>0</td><td>Registros para pular</td></tr>
                  <tr><td><code>limit</code></td><td>integer</td><td>10000</td><td>Máximo de registros</td></tr>
                  <tr><td><code>sort_by</code></td><td>string</td><td>created_at</td><td>Campo: key | created_at</td></tr>
                  <tr><td><code>sort_order</code></td><td>string</td><td>desc</td><td>Ordem: asc | desc</td></tr>
                </tbody>
              </table>

              <h4>Response (200 OK)</h4>
              <pre className="code-block">
                <code>{JSON.stringify([{
                  _id: "507f1f77bcf86cd799439013",
                  key: "john_doe",
                  organization_id: "507f1f77bcf86cd799439011",
                  active: true,
                  image_url: "/images/org_507f.../main.jpg",
                  images: [],
                  embedding_ids: [],
                  created_at: "2025-01-01T00:00:00Z"
                }], null, 2)}</code>
              </pre>

              <h4>Exemplo</h4>
              <div className="code-container">
                <button className="copy-btn" onClick={() => copyToClipboard(getCode('/organizations/507f1f77bcf86cd799439011/people?sort_by=key&sort_order=asc', 'GET'))}>📋 Copiar</button>
                <pre className="code-block"><code>{getCode('/organizations/507f1f77bcf86cd799439011/people?sort_by=key&sort_order=asc', 'GET')}</code></pre>
              </div>

              <h4>Exemplo por key da organização</h4>
              <div className="code-container">
                <button className="copy-btn" onClick={() => copyToClipboard(getCode('/organizations/key/erp_cliente_123/people?sort_by=key&sort_order=asc', 'GET'))}>📋 Copiar</button>
                <pre className="code-block"><code>{getCode('/organizations/key/erp_cliente_123/people?sort_by=key&sort_order=asc', 'GET')}</code></pre>
              </div>
            </div>

            <div className="endpoint-card">
              <div className="endpoint-header">
                <span className="method method-get">GET</span>
                <code className="endpoint-path">/api/organizations/{'{organization_id}'}/people/{'{person_id}'}</code>
              </div>
              <div className="endpoint-header">
                <span className="method method-get">GET</span>
                <code className="endpoint-path">/api/organizations/key/{'{organization_key}'}/people/{'{person_key}'}</code>
              </div>
              <p className="endpoint-description">Obtém detalhes de uma pessoa.</p>

              <h4>Exemplo</h4>
              <div className="code-container">
                <button className="copy-btn" onClick={() => copyToClipboard(getCode('/organizations/507f1f77bcf86cd799439011/people/507f1f77bcf86cd799439013', 'GET'))}>📋 Copiar</button>
                <pre className="code-block"><code>{getCode('/organizations/507f1f77bcf86cd799439011/people/507f1f77bcf86cd799439013', 'GET')}</code></pre>
              </div>

              <h4>Exemplo por keys</h4>
              <div className="code-container">
                <button className="copy-btn" onClick={() => copyToClipboard(getCode('/organizations/key/erp_cliente_123/people/john_doe', 'GET'))}>📋 Copiar</button>
                <pre className="code-block"><code>{getCode('/organizations/key/erp_cliente_123/people/john_doe', 'GET')}</code></pre>
              </div>
            </div>

            <div className="endpoint-card">
              <div className="endpoint-header">
                <span className="method method-post">POST</span>
                <code className="endpoint-path">/api/organizations/{'{organization_id}'}/people</code>
              </div>
              <div className="endpoint-header">
                <span className="method method-post">POST</span>
                <code className="endpoint-path">/api/organizations/key/{'{organization_key}'}/people</code>
              </div>
              <p className="endpoint-description">Cria uma nova pessoa. A chave (key) deve ser única na organização — duplicatas retornam 409.</p>

              <h4>Request Body</h4>
              <pre className="code-block">
                <code>{JSON.stringify({
                  key: "john_doe",
                  organization_id: "507f1f77bcf86cd799439011",
                  active: true
                }, null, 2)}</code>
              </pre>

              <h4>Response (201 Created)</h4>
              <pre className="code-block">
                <code>{JSON.stringify({
                  _id: "507f1f77bcf86cd799439013",
                  key: "john_doe",
                  organization_id: "507f1f77bcf86cd799439011",
                  active: true,
                  image_url: null,
                  images: [],
                  embedding_ids: [],
                  created_at: "2025-01-01T00:00:00Z"
                }, null, 2)}</code>
              </pre>

              <h4>Exemplo</h4>
              <div className="code-container">
                <button className="copy-btn" onClick={() => copyToClipboard(getCode('/organizations/507f1f77bcf86cd799439011/people', 'POST', {
                  key: "john_doe", organization_id: "507f1f77bcf86cd799439011", active: true
                }))}>📋 Copiar</button>
                <pre className="code-block"><code>{getCode('/organizations/507f1f77bcf86cd799439011/people', 'POST', {
                  key: "john_doe", organization_id: "507f1f77bcf86cd799439011", active: true
                })}</code></pre>
              </div>

              <h4>Exemplo por key da organização</h4>
              <div className="code-container">
                <button className="copy-btn" onClick={() => copyToClipboard(getCode('/organizations/key/erp_cliente_123/people', 'POST', {
                  key: "john_doe", organization_id: "erp_cliente_123", active: true
                }))}>📋 Copiar</button>
                <pre className="code-block"><code>{getCode('/organizations/key/erp_cliente_123/people', 'POST', {
                  key: "john_doe", organization_id: "erp_cliente_123", active: true
                })}</code></pre>
              </div>
            </div>

            <div className="endpoint-card">
              <div className="endpoint-header">
                <span className="method method-put">PUT</span>
                <code className="endpoint-path">/api/organizations/{'{organization_id}'}/people/{'{person_id}'}</code>
              </div>
              <div className="endpoint-header">
                <span className="method method-put">PUT</span>
                <code className="endpoint-path">/api/organizations/key/{'{organization_key}'}/people/{'{person_key}'}</code>
              </div>
              <p className="endpoint-description">Atualiza uma pessoa. Campos opcionais: key, active.</p>

              <h4>Exemplo</h4>
              <div className="code-container">
                <button className="copy-btn" onClick={() => copyToClipboard(getCode('/organizations/507f1f77bcf86cd799439011/people/507f1f77bcf86cd799439013', 'PUT', { key: "john_doe_updated" }))}>📋 Copiar</button>
                <pre className="code-block"><code>{getCode('/organizations/507f1f77bcf86cd799439011/people/507f1f77bcf86cd799439013', 'PUT', { key: "john_doe_updated" })}</code></pre>
              </div>

              <h4>Exemplo por keys</h4>
              <div className="code-container">
                <button className="copy-btn" onClick={() => copyToClipboard(getCode('/organizations/key/erp_cliente_123/people/john_doe', 'PUT', { key: "john_doe_updated" }))}>📋 Copiar</button>
                <pre className="code-block"><code>{getCode('/organizations/key/erp_cliente_123/people/john_doe', 'PUT', { key: "john_doe_updated" })}</code></pre>
              </div>
            </div>

            <div className="endpoint-card">
              <div className="endpoint-header">
                <span className="method method-delete">DELETE</span>
                <code className="endpoint-path">/api/organizations/{'{organization_id}'}/people/{'{person_id}'}</code>
              </div>
              <div className="endpoint-header">
                <span className="method method-delete">DELETE</span>
                <code className="endpoint-path">/api/organizations/key/{'{organization_key}'}/people/{'{person_key}'}</code>
              </div>
              <p className="endpoint-description">Remove uma pessoa, suas imagens e embeddings do Milvus.</p>
              <h4>Response (204 No Content)</h4>
              <h4>Exemplo</h4>
              <div className="code-container">
                <button className="copy-btn" onClick={() => copyToClipboard(getCode('/organizations/507f1f77bcf86cd799439011/people/507f1f77bcf86cd799439013', 'DELETE'))}>📋 Copiar</button>
                <pre className="code-block"><code>{getCode('/organizations/507f1f77bcf86cd799439011/people/507f1f77bcf86cd799439013', 'DELETE')}</code></pre>
              </div>

              <h4>Exemplo por keys</h4>
              <div className="code-container">
                <button className="copy-btn" onClick={() => copyToClipboard(getCode('/organizations/key/erp_cliente_123/people/john_doe', 'DELETE'))}>📋 Copiar</button>
                <pre className="code-block"><code>{getCode('/organizations/key/erp_cliente_123/people/john_doe', 'DELETE')}</code></pre>
              </div>
            </div>

            <h2 style={{marginTop: '40px'}}>Imagens</h2>
            <p>Upload, substituição, remoção e validação de imagens faciais. As imagens são validadas automaticamente.</p>

            <div className="info-box">
              <h3>🔍 Validações automáticas</h3>
              <p>Ao fazer upload, a imagem passa por diversas validações:</p>
              <ul>
                <li>Detecção de rosto (pelo menos 1 rosto deve ser visível)</li>
                <li>Tamanho mínimo do rosto</li>
                <li>Qualidade (nitidez, iluminação, contraste)</li>
                <li>Posição frontal (olhos visíveis, rosto voltado para câmera)</li>
                <li>Anti-spoofing (detecção de fotos de fotos)</li>
              </ul>
              <p style={{marginTop: '10px'}}>
                ⚠️ Em caso de erro, consulte a seção{' '}
                <button className="link-button" onClick={() => setActiveSection('errors')}>Erros</button>
                {' '}para ver todos os tipos de erro de imagem possíveis.
              </p>
            </div>

            <div className="endpoint-card">
              <div className="endpoint-header">
                <span className="method method-post">POST</span>
                <code className="endpoint-path">/api/organizations/{'{organization_id}'}/people/{'{person_id}'}/upload-image</code>
              </div>
              <div className="endpoint-header">
                <span className="method method-post">POST</span>
                <code className="endpoint-path">/api/organizations/key/{'{organization_key}'}/people/{'{person_key}'}/upload-image</code>
              </div>
              <p className="endpoint-description">Faz upload de uma imagem facial. Extrai embedding e insere no Milvus.</p>

              <h4>Query Parameters</h4>
              <table className="params-table">
                <thead><tr><th>Parâmetro</th><th>Tipo</th><th>Padrão</th><th>Descrição</th></tr></thead>
                <tbody>
                  <tr><td><code>set_as_main</code></td><td>boolean</td><td>true</td><td>Define como imagem principal</td></tr>
                  <tr><td><code>validation</code></td><td>boolean</td><td>true</td><td>Ativar validação (qualidade, blur, spoof, etc)</td></tr>
                </tbody>
              </table>

              <h4>Request Body (multipart/form-data)</h4>
              <pre className="code-block"><code>file: image.jpg (binary)</code></pre>

              <h4>Response (200 OK)</h4>
              <pre className="code-block">
                <code>{JSON.stringify({
                  message: "Imagem carregada com sucesso",
                  filename: "abc123.jpg",
                  image_url: "/images/org_507f.../abc123.jpg",
                  is_main: true
                }, null, 2)}</code>
              </pre>

              <h4>Exemplo</h4>
              <div className="code-container">
                <button className="copy-btn" onClick={() => copyToClipboard(getCode('/organizations/507f.../people/507f.../upload-image?set_as_main=true&validation=true', 'POST', null, true))}>📋 Copiar</button>
                <pre className="code-block"><code>{getCode('/organizations/507f.../people/507f.../upload-image?set_as_main=true&validation=true', 'POST', null, true)}</code></pre>
              </div>

              <h4>Exemplo por keys</h4>
              <div className="code-container">
                <button className="copy-btn" onClick={() => copyToClipboard(getCode('/organizations/key/erp_cliente_123/people/john_doe/upload-image?set_as_main=true&validation=true', 'POST', null, true))}>📋 Copiar</button>
                <pre className="code-block"><code>{getCode('/organizations/key/erp_cliente_123/people/john_doe/upload-image?set_as_main=true&validation=true', 'POST', null, true)}</code></pre>
              </div>
            </div>

            <div className="endpoint-card">
              <div className="endpoint-header">
                <span className="method method-post">POST</span>
                <code className="endpoint-path">/api/organizations/{'{organization_id}'}/people/{'{person_id}'}/upload-images-batch</code>
              </div>
              <div className="endpoint-header">
                <span className="method method-post">POST</span>
                <code className="endpoint-path">/api/organizations/key/{'{organization_key}'}/people/{'{person_key}'}/upload-images-batch</code>
              </div>
              <p className="endpoint-description">Upload de múltiplas imagens em batch. Cada imagem é validada individualmente.</p>

              <h4>Query Parameters</h4>
              <table className="params-table">
                <thead><tr><th>Parâmetro</th><th>Tipo</th><th>Padrão</th><th>Descrição</th></tr></thead>
                <tbody>
                  <tr><td><code>set_first_as_main</code></td><td>boolean</td><td>true</td><td>Primeira imagem bem-sucedida será a principal</td></tr>
                  <tr><td><code>validation</code></td><td>boolean</td><td>true</td><td>Ativar validação (qualidade, blur, spoof, etc)</td></tr>
                </tbody>
              </table>

              <h4>Request Body (multipart/form-data)</h4>
              <pre className="code-block"><code>files: [image1.jpg, image2.jpg, ...] (binary)</code></pre>

              <h4>Response (200 OK)</h4>
              <pre className="code-block">
                <code>{JSON.stringify({
                  success: true,
                  message: "2 de 3 imagens processadas com sucesso",
                  total: 3,
                  successful: 2,
                  failed: 1,
                  processing_time: "2.34s",
                  successful_images: [{ filename: "img1.jpg", image_url: "/images/..." }],
                  failed_images: [{ filename: "img3.jpg", error: "Nenhum rosto detectável" }]
                }, null, 2)}</code>
              </pre>

              <h4>Exemplo</h4>
              <div className="code-container">
                <button className="copy-btn" onClick={() => copyToClipboard(getCode('/organizations/507f.../people/507f.../upload-images-batch?set_first_as_main=true', 'POST', null, true, ['files=@image1.jpg', 'files=@image2.jpg']))}>📋 Copiar</button>
                <pre className="code-block"><code>{getCode('/organizations/507f.../people/507f.../upload-images-batch?set_first_as_main=true', 'POST', null, true, ['files=@image1.jpg', 'files=@image2.jpg'])}</code></pre>
              </div>

              <h4>Exemplo por keys</h4>
              <div className="code-container">
                <button className="copy-btn" onClick={() => copyToClipboard(getCode('/organizations/key/erp_cliente_123/people/john_doe/upload-images-batch?set_first_as_main=true', 'POST', null, true, ['files=@image1.jpg', 'files=@image2.jpg']))}>📋 Copiar</button>
                <pre className="code-block"><code>{getCode('/organizations/key/erp_cliente_123/people/john_doe/upload-images-batch?set_first_as_main=true', 'POST', null, true, ['files=@image1.jpg', 'files=@image2.jpg'])}</code></pre>
              </div>
            </div>

            <div className="endpoint-card">
              <div className="endpoint-header">
                <span className="method method-post">POST</span>
                <code className="endpoint-path">/api/organizations/{'{organization_id}'}/people/{'{person_id}'}/replace-image</code>
              </div>
              <div className="endpoint-header">
                <span className="method method-post">POST</span>
                <code className="endpoint-path">/api/organizations/key/{'{organization_key}'}/people/{'{person_key}'}/replace-image</code>
              </div>
              <p className="endpoint-description">Substitui uma imagem existente por uma nova.</p>

              <h4>Query Parameters</h4>
              <table className="params-table">
                <thead><tr><th>Parâmetro</th><th>Tipo</th><th>Padrão</th><th>Descrição</th></tr></thead>
                <tbody>
                  <tr><td><code>image_index</code></td><td>integer</td><td>0</td><td>Índice da imagem a substituir</td></tr>
                  <tr><td><code>validation</code></td><td>boolean</td><td>true</td><td>Ativar validação (qualidade, blur, spoof, etc)</td></tr>
                </tbody>
              </table>

              <h4>Request Body (multipart/form-data)</h4>
              <pre className="code-block"><code>file: new_image.jpg (binary)</code></pre>

              <h4>Response (200 OK)</h4>
              <pre className="code-block">
                <code>{JSON.stringify({
                  message: "Imagem substituída com sucesso",
                  filename: "new_abc123.jpg",
                  image_url: "/images/org_507f.../new_abc123.jpg",
                  index: 0
                }, null, 2)}</code>
              </pre>

              <h4>Exemplo</h4>
              <div className="code-container">
                <button className="copy-btn" onClick={() => copyToClipboard(getCode('/organizations/507f.../people/507f.../replace-image?image_index=0&validation=true', 'POST', null, true))}>📋 Copiar</button>
                <pre className="code-block"><code>{getCode('/organizations/507f.../people/507f.../replace-image?image_index=0&validation=true', 'POST', null, true)}</code></pre>
              </div>

              <h4>Exemplo por keys</h4>
              <div className="code-container">
                <button className="copy-btn" onClick={() => copyToClipboard(getCode('/organizations/key/erp_cliente_123/people/john_doe/replace-image?image_index=0&validation=true', 'POST', null, true))}>📋 Copiar</button>
                <pre className="code-block"><code>{getCode('/organizations/key/erp_cliente_123/people/john_doe/replace-image?image_index=0&validation=true', 'POST', null, true)}</code></pre>
              </div>
            </div>

            <div className="endpoint-card">
              <div className="endpoint-header">
                <span className="method method-post">POST</span>
                <code className="endpoint-path">/api/organizations/{'{organization_id}'}/people/{'{person_id}'}/set-main-image</code>
              </div>
              <div className="endpoint-header">
                <span className="method method-post">POST</span>
                <code className="endpoint-path">/api/organizations/key/{'{organization_key}'}/people/{'{person_key}'}/set-main-image</code>
              </div>
              <p className="endpoint-description">Define qual imagem será a principal (usada nas listagens).</p>

              <h4>Query Parameters</h4>
              <table className="params-table">
                <thead><tr><th>Parâmetro</th><th>Tipo</th><th>Obrigatório</th><th>Descrição</th></tr></thead>
                <tbody>
                  <tr><td><code>image_url</code></td><td>string</td><td>Sim</td><td>URL da imagem a definir como principal</td></tr>
                </tbody>
              </table>

              <h4>Response (200 OK)</h4>
              <pre className="code-block">
                <code>{JSON.stringify({ message: "Imagem principal atualizada", image_url: "/images/org_507f.../abc123.jpg" }, null, 2)}</code>
              </pre>

              <h4>Exemplo</h4>
              <div className="code-container">
                <button className="copy-btn" onClick={() => copyToClipboard(getCode('/organizations/507f.../people/507f.../set-main-image?image_url=/images/abc123.jpg', 'POST'))}>📋 Copiar</button>
                <pre className="code-block"><code>{getCode('/organizations/507f.../people/507f.../set-main-image?image_url=/images/abc123.jpg', 'POST')}</code></pre>
              </div>

              <h4>Exemplo por keys</h4>
              <div className="code-container">
                <button className="copy-btn" onClick={() => copyToClipboard(getCode('/organizations/key/erp_cliente_123/people/john_doe/set-main-image?image_url=/images/abc123.jpg', 'POST'))}>📋 Copiar</button>
                <pre className="code-block"><code>{getCode('/organizations/key/erp_cliente_123/people/john_doe/set-main-image?image_url=/images/abc123.jpg', 'POST')}</code></pre>
              </div>
            </div>

            <div className="endpoint-card">
              <div className="endpoint-header">
                <span className="method method-delete">DELETE</span>
                <code className="endpoint-path">/api/organizations/{'{organization_id}'}/people/{'{person_id}'}/images/{'{image_filename}'}</code>
              </div>
              <div className="endpoint-header">
                <span className="method method-delete">DELETE</span>
                <code className="endpoint-path">/api/organizations/key/{'{organization_key}'}/people/{'{person_key}'}/images/{'{image_filename}'}</code>
              </div>
              <p className="endpoint-description">Remove uma imagem, seu embedding do Milvus e o arquivo do disco.</p>

              <h4>Exemplo</h4>
              <div className="code-container">
                <button className="copy-btn" onClick={() => copyToClipboard(getCode('/organizations/507f.../people/507f.../images/abc123.jpg', 'DELETE'))}>📋 Copiar</button>
                <pre className="code-block"><code>{getCode('/organizations/507f.../people/507f.../images/abc123.jpg', 'DELETE')}</code></pre>
              </div>

              <h4>Exemplo por keys</h4>
              <div className="code-container">
                <button className="copy-btn" onClick={() => copyToClipboard(getCode('/organizations/key/erp_cliente_123/people/john_doe/images/abc123.jpg', 'DELETE'))}>📋 Copiar</button>
                <pre className="code-block"><code>{getCode('/organizations/key/erp_cliente_123/people/john_doe/images/abc123.jpg', 'DELETE')}</code></pre>
              </div>
            </div>

            <div className="endpoint-card">
              <div className="endpoint-header">
                <span className="method method-post">POST</span>
                <code className="endpoint-path">/api/people/validate-face</code>
              </div>
              <p className="endpoint-description">Valida se uma imagem contém um rosto válido sem cadastrá-la em uma pessoa.</p>

              <h4>Query Parameters</h4>
              <table className="params-table">
                <thead><tr><th>Parâmetro</th><th>Tipo</th><th>Padrão</th><th>Descrição</th></tr></thead>
                <tbody>
                  <tr><td><code>organization_id</code></td><td>string</td><td>obrigatório</td><td>ID da organização à qual a validação pertence</td></tr>
                  <tr><td><code>validation</code></td><td>boolean</td><td>true</td><td>Ativar validação (qualidade, blur, spoof, etc)</td></tr>
                </tbody>
              </table>

              <h4>Request Body (multipart/form-data)</h4>
              <pre className="code-block"><code>file: image.jpg (binary)</code></pre>

              <h4>Response (200 OK)</h4>
              <pre className="code-block">
                <code>{JSON.stringify({
                  has_face: true,
                  message: "Face válida detectada",
                  embedding_size: 512
                }, null, 2)}</code>
              </pre>
            </div>
          </section>
        )}

        {/* ==================== BUSCA FACIAL ==================== */}
        {activeSection === 'search' && (
          <section>
            <h1>Busca Facial</h1>
            <p>Pesquisa por similaridade facial e comparação de imagens.</p>

            <div className="warning-box">
              <h4>⚠️ Erros de imagem</h4>
              <p>
                Os endpoints de busca facial validam a imagem enviada. Em caso de erro, consulte a seção{' '}
                <button className="link-button" onClick={() => setActiveSection('errors')}>Erros</button>
                {' '}para ver todos os tipos de erro possíveis.
              </p>
            </div>

            <div className="endpoint-card">
              <div className="endpoint-header">
                <span className="method method-post">POST</span>
                <code className="endpoint-path">/api/search</code>
              </div>
              <p className="endpoint-description">Busca pessoas por reconhecimento facial. Envie uma imagem OU um query_image_id (de busca anterior).</p>

              <h4>Query Parameters</h4>
              <table className="params-table">
                <thead><tr><th>Parâmetro</th><th>Tipo</th><th>Obrigatório</th><th>Descrição</th></tr></thead>
                <tbody>
                  <tr><td><code>organization_id</code></td><td>string</td><td>Não</td><td>ID da organização para buscar (alternativa a organization_key)</td></tr>
                  <tr><td><code>organization_key</code></td><td>string</td><td>Não</td><td>Key da organização para buscar (alternativa a organization_id)</td></tr>
                  <tr><td><code>person_id</code></td><td>string</td><td>Não</td><td>Filtrar por pessoa específica</td></tr>
                  <tr><td><code>person_key</code></td><td>string</td><td>Não</td><td>Filtrar por key da pessoa (alternativa a person_id)</td></tr>
                  <tr><td><code>top</code></td><td>integer</td><td>Não</td><td>Quantidade máxima de resultados retornados (padrão: 10, min: 1, max: 100)</td></tr>
                  <tr><td><code>detector</code></td><td>string</td><td>Não</td><td>Detector: ssd | retinaface | opencv | mtcnn</td></tr>
                  <tr><td><code>validation</code></td><td>boolean</td><td>Não</td><td>Validar qualidade da imagem (padrão: true)</td></tr>
                  <tr><td><code>query_image_id</code></td><td>string</td><td>Não</td><td>ID de imagem já enviada anteriormente (reutilizar embedding)</td></tr>
                </tbody>
              </table>

              <p><strong>Regra:</strong> envie <code>organization_id</code> ou <code>organization_key</code> (não ambos).</p>
              <p><strong>Regra:</strong> se filtrar pessoa, envie <code>person_id</code> ou <code>person_key</code> (não ambos).</p>

              <h4>Request Body (multipart/form-data)</h4>
              <pre className="code-block"><code>file: image.jpg (binary) — obrigatório se query_image_id não for fornecido</code></pre>

              <h4>Response (200 OK)</h4>
              <pre className="code-block">
                <code>{JSON.stringify({
                  query_image: "/tmp/face_images/abc123.jpg",
                  query_image_id: "abc123",
                  faces_detected: 1,
                  results: [{
                    person_id: "507f1f77bcf86cd799439013",
                    key: "john_doe",
                    organization_id: "507f1f77bcf86cd799439011",
                    organization_name: "Acme Corp",
                    similarity_score: 0.95,
                    distance: 0.12,
                    image_url: "/images/org_507f.../main.jpg",
                    image_id: "img_001"
                  }],
                  search_time_ms: 125.5,
                  upload_io_time_ms: 15.2,
                  validation_quality_time_ms: 30.1,
                  embedding_time_ms: 45.3,
                  milvus_search_time_ms: 10.5,
                  mongodb_search_time_ms: 5.2,
                  unaccounted_time_ms: 1.2,
                  spoof_check_score: 12.6,
                  warning: {
                    error_type: "spoofing_detected",
                    message: "Imagem suspeita de falsificação — score de falsidade: 12.6%",
                    spoof_false_score_pct: 12.6,
                    spoof_limit_pct: 30.0
                  }
                }, null, 2)}</code>
              </pre>

              <h4>Observações da resposta</h4>
              <ul>
                <li><code>warning</code> é um objeto único opcional (ou <code>null</code>), não uma lista.</li>
                <li><code>warning</code> aparece quando a validação detecta condição degradada e a busca segue com fallback.</li>
                <li><code>spoof_check_score</code> representa percentual de falsidade (0 a 100), quando disponível.</li>
              </ul>

              <div className="info-box">
                <h4>📊 Similarity Score</h4>
                <p>O score de similaridade varia de 0 a 1:</p>
                <ul>
                  <li><strong>0.9 – 1.0</strong>: Match muito alto (mesma pessoa)</li>
                  <li><strong>0.7 – 0.9</strong>: Match alto (provavelmente a mesma pessoa)</li>
                  <li><strong>0.5 – 0.7</strong>: Match médio (possível match)</li>
                  <li><strong>0.0 – 0.5</strong>: Match baixo (pessoas diferentes)</li>
                </ul>
              </div>

              <h4>Exemplo</h4>
              <div className="code-container">
                <button className="copy-btn" onClick={() => copyToClipboard(getCode('/search?organization_id=507f...&top=5&detector=mtcnn', 'POST', null, true))}>📋 Copiar</button>
                <pre className="code-block"><code>{getCode('/search?organization_id=507f...&top=5&detector=mtcnn', 'POST', null, true)}</code></pre>
              </div>

              <h4>Exemplo por keys</h4>
              <div className="code-container">
                <button className="copy-btn" onClick={() => copyToClipboard(getCode('/search?organization_key=erp_cliente_123&person_key=john_doe&top=3&detector=mtcnn', 'POST', null, true))}>📋 Copiar</button>
                <pre className="code-block"><code>{getCode('/search?organization_key=erp_cliente_123&person_key=john_doe&top=3&detector=mtcnn', 'POST', null, true)}</code></pre>
              </div>
            </div>

            <div className="endpoint-card">
              <div className="endpoint-header">
                <span className="method method-post">POST</span>
                <code className="endpoint-path">/api/compare-images</code>
              </div>
              <p className="endpoint-description">Compara duas imagens diretamente e retorna o score de similaridade.</p>

              <h4>Query Parameters</h4>
              <table className="params-table">
                <thead><tr><th>Parâmetro</th><th>Tipo</th><th>Padrão</th><th>Descrição</th></tr></thead>
                <tbody>
                  <tr><td><code>detector</code></td><td>string</td><td>—</td><td>Detector: ssd | retinaface | opencv | mtcnn</td></tr>
                  <tr><td><code>validation</code></td><td>boolean</td><td>true</td><td>Validar qualidade das imagens</td></tr>
                  <tr><td><code>threshold</code></td><td>float</td><td>0.7</td><td>Limiar de compatibilidade (0 a 1)</td></tr>
                </tbody>
              </table>

              <h4>Request Body (multipart/form-data)</h4>
              <pre className="code-block"><code>file1: image1.jpg (binary)
file2: image2.jpg (binary)</code></pre>

              <h4>Response (200 OK)</h4>
              <pre className="code-block">
                <code>{JSON.stringify({
                  similarity_score: 0.92,
                  distance: 0.15,
                  compatible: true,
                  threshold: 0.7
                }, null, 2)}</code>
              </pre>

              <h4>Exemplo</h4>
              <div className="code-container">
                <button className="copy-btn" onClick={() => copyToClipboard(getCode('/compare-images?threshold=0.7', 'POST', null, true, ['file1=@image1.jpg', 'file2=@image2.jpg']))}>📋 Copiar</button>
                <pre className="code-block"><code>{getCode('/compare-images?threshold=0.7', 'POST', null, true, ['file1=@image1.jpg', 'file2=@image2.jpg'])}</code></pre>
              </div>
            </div>

          </section>
        )}

        {/* ==================== ERROS ==================== */}
        {activeSection === 'errors' && (
          <section>
            <h1>Códigos de Erro</h1>
            <p>A API utiliza códigos de status HTTP padrão.</p>

            <table className="error-table">
              <thead>
                <tr><th>Código</th><th>Status</th><th>Descrição</th></tr>
              </thead>
              <tbody>
                <tr className="success"><td>200</td><td>OK</td><td>Requisição bem-sucedida</td></tr>
                <tr className="success"><td>201</td><td>Created</td><td>Recurso criado com sucesso</td></tr>
                <tr className="success"><td>204</td><td>No Content</td><td>Requisição bem-sucedida sem conteúdo</td></tr>
                <tr className="error"><td>400</td><td>Bad Request</td><td>Requisição inválida (imagem sem rosto, formato errado, etc.)</td></tr>
                <tr className="error"><td>401</td><td>Unauthorized</td><td>Token ausente ou inválido</td></tr>
                <tr className="error"><td>404</td><td>Not Found</td><td>Recurso não encontrado</td></tr>
                <tr className="error"><td>409</td><td>Conflict</td><td>Registro duplicado (nome de token/instância, chave de organização/pessoa)</td></tr>
                <tr className="error"><td>500</td><td>Internal Error</td><td>Erro interno do servidor</td></tr>
              </tbody>
            </table>

            <h3>Formato de Erro Padrão</h3>
            <pre className="code-block">
              <code>{JSON.stringify({ detail: "Mensagem descritiva do erro" }, null, 2)}</code>
            </pre>

            <h3>Formato de Erro de Imagem</h3>
            <p>Upload de imagens retorna erros estruturados com tipo:</p>
            <pre className="code-block">
              <code>{JSON.stringify({
                detail: {
                  error_type: "no_face_detectable",
                  message: "Nenhum rosto detectável na imagem"
                }
              }, null, 2)}</code>
            </pre>

            <div className="info-box">
              <h4>Tipos de erro de imagem</h4>
              <table className="params-table">
                <thead><tr><th>error_type</th><th>Descrição</th></tr></thead>
                <tbody>
                  <tr><td><code>no_face_detectable</code></td><td>Nenhum rosto encontrado</td></tr>
                  <tr><td><code>multiple_faces</code></td><td>Mais de um rosto na imagem</td></tr>
                  <tr><td><code>face_too_small</code></td><td>Rosto muito pequeno</td></tr>
                  <tr><td><code>face_too_far</code></td><td>Rosto muito distante</td></tr>
                  <tr><td><code>face_cropped</code></td><td>Rosto cortado</td></tr>
                  <tr><td><code>face_off_center</code></td><td>Rosto fora do centro</td></tr>
                  <tr><td><code>blurry_face</code></td><td>Imagem borrada</td></tr>
                  <tr><td><code>not_facing_camera</code></td><td>Rosto não frontal</td></tr>
                  <tr><td><code>eyes_not_visible</code></td><td>Olhos não visíveis</td></tr>
                  <tr><td><code>poor_lighting_dark</code></td><td>Iluminação insuficiente</td></tr>
                  <tr><td><code>poor_lighting_overexposed</code></td><td>Imagem superexposta</td></tr>
                  <tr><td><code>spoofing_detected</code></td><td>Possível fraude (foto de foto)</td></tr>
                  <tr><td><code>image_too_small</code></td><td>Imagem muito pequena</td></tr>
                  <tr><td><code>image_read_error</code></td><td>Erro ao ler a imagem</td></tr>
                </tbody>
              </table>
            </div>
          </section>
        )}
        {/* ==================== CONFIGURAÇÃO ==================== */}
        {activeSection === 'config' && (
          <section>
            <h1>Configuração</h1>
            <p>
              O comportamento da API pode ser ajustado via variáveis de ambiente no arquivo <code>.env</code> do backend
              ou diretamente como variáveis de ambiente no container.
            </p>

            <h2 style={{marginTop: '32px'}}>Detector e Modelo</h2>

            <table className="params-table">
              <thead><tr><th>Variável</th><th>Padrão</th><th>Descrição</th></tr></thead>
              <tbody>
                <tr><td><code>FACE_DETECTOR</code></td><td>retinaface</td><td>Detector usado em todas as operações: opencv | ssd | mtcnn | retinaface. Na busca facial, o usuário pode sobrescrever via query param <code>?detector=...</code></td></tr>
                <tr><td><code>ENABLE_TTA_MIRROR</code></td><td>false</td><td>Gera somente o embedding original por padrão. Use true para combinar a face original e espelhada; a troca exige reindexação.</td></tr>
                <tr><td><code>EMBEDDING_INFERENCE_MODE</code></td><td>predict_on_batch</td><td>Reutiliza o grafo TensorFlow compilado no Facenet512. Use eager somente para rollback/diagnóstico.</td></tr>
                <tr><td><code>TF_ENABLE_ONEDNN_OPTS</code></td><td>0</td><td>Desativa oneDNN; foi mais rápido no Ryzen 7 5700G usado no desenvolvimento. Reavalie em outro processador.</td></tr>
                <tr><td><code>ML_CONCURRENCY</code></td><td>1</td><td>Máximo de operações de ML simultâneas por worker (1 = sequencial, mais estável)</td></tr>
              </tbody>
            </table>

            <h2 style={{marginTop: '32px'}}>Thresholds de Validação de Imagem</h2>
            <p>
              Controlam as regras aplicadas em todo upload de imagem (e durante a busca facial).
              Ajuste estes valores para tornar a validação mais permissiva ou mais rigorosa conforme o seu caso de uso.
            </p>

            <h3 style={{marginTop: '24px'}}>Geometria e Tamanho</h3>
            <table className="params-table">
              <thead><tr><th>Variável</th><th>Padrão</th><th>Descrição</th></tr></thead>
              <tbody>
                <tr>
                  <td><code>VAL_MIN_IMAGE_SIZE</code></td><td>180</td>
                  <td>Dimensão mínima da imagem em pixels (largura ou altura). Imagens menores são rejeitadas com <code>image_too_small</code>.</td>
                </tr>
                <tr>
                  <td><code>VAL_MIN_FACE_SIZE</code></td><td>80</td>
                  <td>Dimensão mínima do rosto detectado em pixels. Rostos menores retornam <code>face_too_small</code>.</td>
                </tr>
                <tr>
                  <td><code>VAL_MIN_FACE_AREA_RATIO</code></td><td>0.05</td>
                  <td>Fração mínima da imagem que o rosto deve ocupar (5%). Rostos muito distantes retornam <code>face_too_far</code>.</td>
                </tr>
              </tbody>
            </table>

            <h3 style={{marginTop: '24px'}}>Enquadramento e Centralização</h3>
            <table className="params-table">
              <thead><tr><th>Variável</th><th>Padrão</th><th>Descrição</th></tr></thead>
              <tbody>
                <tr>
                  <td><code>VAL_MAX_FACE_OFFSET_X</code></td><td>0.30</td>
                  <td>Desvio horizontal máximo do rosto em relação ao centro da imagem (0–1). Valores acima retornam <code>face_off_center</code>.</td>
                </tr>
                <tr>
                  <td><code>VAL_MAX_FACE_OFFSET_Y</code></td><td>0.34</td>
                  <td>Desvio vertical máximo do rosto em relação ao centro da imagem (0–1). Valores acima retornam <code>face_off_center</code>.</td>
                </tr>
              </tbody>
            </table>

            <h3 style={{marginTop: '24px'}}>Confiança do Detector</h3>
            <table className="params-table">
              <thead><tr><th>Variável</th><th>Padrão</th><th>Descrição</th></tr></thead>
              <tbody>
                <tr>
                  <td><code>VAL_MIN_DETECTION_CONFIDENCE</code></td><td>0.80</td>
                  <td>Confiança mínima retornada pelo detector de faces. Detecções abaixo deste valor são descartadas.</td>
                </tr>
              </tbody>
            </table>

            <h3 style={{marginTop: '24px'}}>Nitidez (Sharpness)</h3>
            <p>Calculada pela variância do filtro Laplaciano — quanto maior, mais nítida a imagem.</p>
            <table className="params-table">
              <thead><tr><th>Variável</th><th>Padrão</th><th>Descrição</th></tr></thead>
              <tbody>
                <tr>
                  <td><code>VAL_MIN_EYE_LAPLACIAN</code></td><td>30.0</td>
                  <td>Nitidez mínima na região dos olhos (patch local). Abaixo disto retorna <code>eyes_not_clear</code>.</td>
                </tr>
                <tr>
                  <td><code>VAL_MIN_EYE_STD</code></td><td>8.0</td>
                  <td>Desvio-padrão mínimo de intensidade na região dos olhos. Complementa a métrica de Laplacian.</td>
                </tr>
                <tr>
                  <td><code>VAL_MIN_FACE_LAPLACIAN</code></td><td>30.0</td>
                  <td>Nitidez mínima geral do rosto. Abaixo disto retorna <code>blurry_face</code>.</td>
                </tr>
                <tr>
                  <td><code>VAL_MIN_EYE_BAND_LAPLACIAN</code></td><td>28.0</td>
                  <td>Nitidez mínima do terço superior do rosto (região dos olhos). Abaixo disto retorna <code>eyes_not_clear</code>.</td>
                </tr>
              </tbody>
            </table>

            <h3 style={{marginTop: '24px'}}>Iluminação</h3>
            <table className="params-table">
              <thead><tr><th>Variável</th><th>Padrão</th><th>Descrição</th></tr></thead>
              <tbody>
                <tr>
                  <td><code>VAL_MIN_FACE_MEAN</code></td><td>60.0</td>
                  <td>Brilho médio mínimo do rosto (escala 0–255). Abaixo disto retorna <code>poor_lighting_dark</code>.</td>
                </tr>
                <tr>
                  <td><code>VAL_MAX_DARK_CLIP_RATIO</code></td><td>0.30</td>
                  <td>Fração máxima de pixels muito escuros ({'<'}25). Imagens com muita sombra retornam <code>poor_lighting_dark</code>.</td>
                </tr>
                <tr>
                  <td><code>VAL_MAX_FACE_MEAN</code></td><td>205.0</td>
                  <td>Brilho médio máximo do rosto (escala 0–255). Acima disto retorna <code>poor_lighting_overexposed</code>.</td>
                </tr>
                <tr>
                  <td><code>VAL_MAX_BRIGHT_CLIP_RATIO</code></td><td>0.20</td>
                  <td>Fração máxima de pixels muito claros ({'>'}245). Imagens superexpostas retornam <code>poor_lighting_overexposed</code>.</td>
                </tr>
              </tbody>
            </table>

            <h3 style={{marginTop: '24px'}}>Contraste</h3>
            <table className="params-table">
              <thead><tr><th>Variável</th><th>Padrão</th><th>Descrição</th></tr></thead>
              <tbody>
                <tr>
                  <td><code>VAL_MIN_FACE_STD</code></td><td>24.0</td>
                  <td>Desvio-padrão mínimo de intensidade do rosto. Imagens sem contraste retornam <code>low_face_contrast</code>.</td>
                </tr>
                <tr>
                  <td><code>VAL_MIN_DYNAMIC_RANGE</code></td><td>75.0</td>
                  <td>Intervalo dinâmico mínimo (percentil 95 − percentil 5). Complementa a métrica de contraste.</td>
                </tr>
              </tbody>
            </table>

            <h2 style={{marginTop: '32px'}}>Anti-Spoofing</h2>
            <p>
              Controles para detectar imagens falsas (foto de foto, tela de celular/monitor, impressões).
              Dois mecanismos atuam em conjunto: análise do DeepFace e verificação de padrões visuais (<code>quick_spoof_check</code>).
            </p>

            <h3 style={{marginTop: '24px'}}>DeepFace Anti-Spoofing</h3>
            <table className="params-table">
              <thead><tr><th>Variável</th><th>Padrão</th><th>Descrição</th></tr></thead>
              <tbody>
                <tr>
                  <td><code>VAL_MIN_ANTISPOOF_REAL_SCORE</code></td><td>0.5</td>
                  <td>Score mínimo para considerar imagem real. O <code>antispoof_score</code> do DeepFace representa probabilidade de ser falsa (0=real, 1=falso). Imagens com score de falsidade acima de <code>1 - valor</code> são rejeitadas com <code>spoofing_detected</code>.</td>
                </tr>
              </tbody>
            </table>

            <h3 style={{marginTop: '24px'}}>Detecção de Moiré (FFT)</h3>
            <p>Padrões de moiré são artefatos de frequência típicos de telas e impressões fotografadas.</p>
            <table className="params-table">
              <thead><tr><th>Variável</th><th>Padrão</th><th>Descrição</th></tr></thead>
              <tbody>
                <tr>
                  <td><code>VAL_SPOOF_MOIRE_THRESHOLD</code></td><td>400</td>
                  <td>Energia FFT mínima para contar como sinal médio de moiré (contribui para <code>medium_signals</code>). Não rejeita sozinho.</td>
                </tr>
                <tr>
                  <td><code>VAL_SPOOF_MOIRE_DEFINITIVE_THRESHOLD</code></td><td>1200</td>
                  <td>Energia FFT para rejeição imediata (sozinha), sem precisar acumular outros sinais. Indica moiré intenso típico de tela.</td>
                </tr>
                <tr>
                  <td><code>VAL_SPOOF_FFT_STD_MULTIPLIER</code></td><td>2.0</td>
                  <td>Multiplicador do desvio-padrão na análise FFT. Pixels com magnitude acima de <code>média + N × desvio</code> são contados como energia de alta frequência.</td>
                </tr>
              </tbody>
            </table>

            <h3 style={{marginTop: '24px'}}>Densidade de Bordas</h3>
            <table className="params-table">
              <thead><tr><th>Variável</th><th>Padrão</th><th>Descrição</th></tr></thead>
              <tbody>
                <tr>
                  <td><code>VAL_SPOOF_MIN_EDGE_DENSITY</code></td><td>0.01</td>
                  <td>Fração mínima de pixels com borda detectada (Canny). Imagens com bordas escassas contam como sinal médio suspeito.</td>
                </tr>
              </tbody>
            </table>

            <h3 style={{marginTop: '24px'}}>Blur (Desfoque Global)</h3>
            <table className="params-table">
              <thead><tr><th>Variável</th><th>Padrão</th><th>Descrição</th></tr></thead>
              <tbody>
                <tr>
                  <td><code>VAL_SPOOF_BLUR_THRESHOLD</code></td><td>15.0</td>
                  <td>Laplaciano abaixo deste valor significa imagem borrada — conta como sinal médio.</td>
                </tr>
                <tr>
                  <td><code>VAL_SPOOF_MEDIUM_BLUR_THRESHOLD</code></td><td>22.0</td>
                  <td>Laplaciano abaixo deste valor conta como blur moderado em <code>medium_signals</code>.</td>
                </tr>
                <tr>
                  <td><code>VAL_SPOOF_STRICT_BLUR_THRESHOLD</code></td><td>12.0</td>
                  <td>Laplaciano abaixo deste valor rejeita imediatamente (imagem muito borrada).</td>
                </tr>
              </tbody>
            </table>

            <h3 style={{marginTop: '24px'}}>Contraste Global</h3>
            <table className="params-table">
              <thead><tr><th>Variável</th><th>Padrão</th><th>Descrição</th></tr></thead>
              <tbody>
                <tr>
                  <td><code>VAL_SPOOF_CONTRAST_THRESHOLD</code></td><td>18.0</td>
                  <td>Desvio-padrão de intensidade abaixo deste valor = baixo contraste, conta como sinal médio.</td>
                </tr>
                <tr>
                  <td><code>VAL_SPOOF_MEDIUM_CONTRAST_THRESHOLD</code></td><td>24.0</td>
                  <td>Contraste abaixo deste valor conta como contraste moderado em <code>medium_signals</code>.</td>
                </tr>
                <tr>
                  <td><code>VAL_SPOOF_STRICT_CONTRAST_THRESHOLD</code></td><td>15.0</td>
                  <td>Contraste abaixo deste valor rejeita imediatamente.</td>
                </tr>
              </tbody>
            </table>

            <h3 style={{marginTop: '24px'}}>Brilho Global</h3>
            <table className="params-table">
              <thead><tr><th>Variável</th><th>Padrão</th><th>Descrição</th></tr></thead>
              <tbody>
                <tr>
                  <td><code>VAL_SPOOF_DARK_THRESHOLD</code></td><td>20.0</td>
                  <td>Brilho médio abaixo deste valor = imagem muito escura, rejeição imediata.</td>
                </tr>
                <tr>
                  <td><code>VAL_SPOOF_OVEREXPOSED_THRESHOLD</code></td><td>240.0</td>
                  <td>Brilho médio acima deste valor = imagem superexposta, rejeição imediata.</td>
                </tr>
                <tr>
                  <td><code>VAL_SPOOF_BRIGHT_SURFACE_THRESHOLD</code></td><td>220.0</td>
                  <td>Brilho médio acima deste valor conta como superfície muito clara em <code>medium_signals</code> (típico de tela).</td>
                </tr>
              </tbody>
            </table>

            <h3 style={{marginTop: '24px'}}>Detecção de Contorno Retangular (Foto Impressa)</h3>
            <p>Detecta bordas retangulares com proporções de foto impressa segurada na mão ou posicionada na cena.</p>
            <table className="params-table">
              <thead><tr><th>Variável</th><th>Padrão</th><th>Descrição</th></tr></thead>
              <tbody>
                <tr>
                  <td><code>VAL_SPOOF_PRINTED_MIN_CONTOUR_AREA_RATIO</code></td><td>0.08</td>
                  <td>Fração mínima da imagem que o contorno deve ter para ser analisado (filtra ruídos pequenos).</td>
                </tr>
                <tr>
                  <td><code>VAL_SPOOF_PRINTED_POLY_EPSILON</code></td><td>0.03</td>
                  <td>Epsilon para aproximação poligonal do contorno (fração do perímetro). Controla sensibilidade na detecção de quadriláteros.</td>
                </tr>
                <tr>
                  <td><code>VAL_SPOOF_PRINTED_MIN_ASPECT</code></td><td>1.20</td>
                  <td>Proporção mínima altura/largura do contorno para considerá-lo uma foto (portrait).</td>
                </tr>
                <tr>
                  <td><code>VAL_SPOOF_PRINTED_MAX_ASPECT</code></td><td>1.70</td>
                  <td>Proporção máxima altura/largura do contorno para considerá-lo uma foto (portrait).</td>
                </tr>
                <tr>
                  <td><code>VAL_SPOOF_PRINTED_MIN_AREA_RATIO</code></td><td>0.12</td>
                  <td>Fração mínima da imagem que o contorno detectado deve ocupar para ser classificado como foto impressa.</td>
                </tr>
              </tbody>
            </table>

            <h3 style={{marginTop: '24px'}}>Acúmulo de Sinais Médios</h3>
            <table className="params-table">
              <thead><tr><th>Variável</th><th>Padrão</th><th>Descrição</th></tr></thead>
              <tbody>
                <tr>
                  <td><code>VAL_SPOOF_MEDIUM_SIGNALS_THRESHOLD</code></td><td>3</td>
                  <td>Quantidade mínima de sinais médios simultâneos para rejeitar a imagem. Sinais médios: blur moderado, baixo contraste, bordas escassas, superfície brilhante, contorno de foto impressa, moiré médio.</td>
                </tr>
              </tbody>
            </table>

          </section>
        )}
      </main>
    </div>
  );
}

export default Docs;
